import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import qs from 'qs';
import SessionStore from 'stores/SessionStore';
import Container from 'components/v2/container';
import { useHistory, useLocation } from 'react-router-dom';
import Alert from 'components/ui/Alert';
import { uniq } from 'underscore';
import { showToast } from 'stores/ToastStore';
import { findUsers } from 'utils/UserWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import { bulkEditCarrierRate, getUnprocessedCarrierBaseCosts, getUnprocessedShipmentList, postCarrierBulkApproveRates, } from '../../../utils/ShipmentWebAPIUtils';
import BulkAuditList from './bulk-audit/List';
import BulkAuditFilters from './bulk-audit/Filters';
import FilterButton from '../../v2/buttons/FilterButton';
import ShipmentListModal from './modals/ShipmentListModal';
import useInterval from '../../../hooks/useInterval';
import usePageVisibility from '../../../hooks/usePageVisibility';
import { getCircleButtonClass } from '../../v2/buttons/CircleButton';
import BulkApprovalModal from './modals/BulkApprovalModal';
import EditLaneRateModal from './modals/EditLaneRateModal';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import CargomaticInput from '../../v2/input';
const PER_PAGE = 10;
const REFRESH_INTERVAL = 30000;
const FILTERS = [
    {
        key: 'carrier',
        customerType: 'carriers',
        type: 'customer',
        name: 'CARRIER',
    },
    {
        key: 'shipper',
        customerType: 'shippers',
        type: 'customer',
        name: 'SHIPPER',
    },
    {
        key: 'start_date',
        name: 'FROM_DATE',
        type: 'date',
    },
    {
        key: 'end_date',
        name: 'TO_DATE',
        type: 'date',
    },
    {
        key: 'origin_address',
        name: 'Origin',
        type: 'location',
    },
    {
        key: 'destination_address',
        name: 'Destination',
        type: 'location',
    },
];
export const SORTING_OPTIONS = [
    {
        value: 'count__desc',
        name: 'COUNT_HIGHEST',
    },
    {
        value: 'count__asc',
        name: 'COUNT_LOWEST',
    },
    {
        value: 'shipper__desc',
        name: 'SHIPPER_DESC',
    },
    {
        value: 'shipper__asc',
        name: 'SHIPPER_ASC',
    },
    {
        value: 'carrier__desc',
        name: 'CARRIER_DESC',
    },
    {
        value: 'carrier__asc',
        name: 'CARRIER_ASC',
    },
    {
        value: 'due_date__asc',
        name: 'DUEDATE_ASC',
        hide: !featureEnabled(CONFIGURED_FEATURES.CCBAT),
    },
];
export default function CarrierBulkAuditTool() {
    const isVisible = usePageVisibility(true);
    const { t } = useTranslation();
    const history = useHistory();
    const thislocation = useLocation();
    const [cancelTokenSource, setCancelTokenSource] = useState();
    const { search } = thislocation;
    const [locationParsed, setLocationParsed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [sortValue, setSortValue] = useState(SORTING_OPTIONS[6].value);
    const [filtersToggled, setFiltersToggled] = useState(false);
    const [shipmentsLoading, setShipmentsLoading] = useState(true);
    const [shipmentsTotal, setShipmentsTotal] = useState(0);
    const [filterValues, setFilterValues] = useState({
        shipper: [],
        carrier: [],
        start_date: moment().subtract(15, 'days'),
        end_date: moment(),
        origin_address: null,
        origin_street: null,
        origin_city: null,
        origin_state: null,
        origin_zipcode: null,
        destination_address: null,
        destination_street: null,
        destination_city: null,
        destination_state: null,
        destination_zipcode: null,
        quickpay_carrier: true,
    });
    const [selectedFiltersCount, setSelectedFiltersCount] = useState(0);
    const [filterMetadata, setFilterMetadata] = useState({
        carrier: [],
        shipper: [],
        origin_address: {},
        destination_address: {},
        quickpay_carrier: true,
    });
    const [selectedRates, setSelectedRates] = useState([]);
    const [showEditRateModal, setShowEditRateModal] = useState(false);
    const [showConfirmBulkApproveModal, setShowConfirmBulkApproveModal] = useState(false);
    const [showViewShipmentsModal, setShowViewShipmentsModal] = useState(false);
    const [unapprovedShipmentList, setUnapprovedShipmentList] = useState([]);
    const [shipmentsQueuedForApproval, setShipmentsQueuedForApproval] = useState([]);
    function cancelRequest() {
        if (cancelTokenSource) {
            cancelTokenSource.cancel();
        }
    }
    useEffect(() => {
        return () => {
            cancelRequest();
        };
    }, []);
    useEffect(() => {
        if (!isVisible) {
            cancelRequest();
        }
    }, [isVisible]);
    useEffect(() => {
        updateSelectedFiltersCount();
        setPage(1);
    }, [filterValues]);
    useEffect(() => {
        setFiltersFromQuery();
    }, [thislocation.pathname, search]);
    useEffect(() => {
        if (locationParsed) {
            setLoading(true);
            fetchUnprocessedCarrierBaseCosts();
        }
    }, [locationParsed, page, sortValue, filterValues]);
    useEffect(() => {
        document.title = t('CARRIER_COST_BULK_AUDIT_TOOL');
    }, []);
    useEffect(() => {
        if (!unapprovedShipmentList.length)
            setShowViewShipmentsModal(false);
    }, [unapprovedShipmentList]);
    async function setFiltersFromQuery() {
        const query = qs.parse(thislocation.search, { ignoreQueryPrefix: true });
        if (query.page && query.page.length > 0)
            setPage(parseInt(query.page, 10) || 1);
        if (query.sort &&
            query.sort.length > 0 &&
            query.sort_dir &&
            query.sort_dir.length > 0) {
            setSortValue([query.sort, query.sort_dir].join('__') || SORTING_OPTIONS[0].value);
        }
        setFilterValues((prev) => {
            const startDate = query.start_date && query.start_date.length > 0
                ? moment(query.start_date)
                : prev.start_date;
            const endDate = query.end_date && query.end_date.length > 0
                ? moment(query.end_date)
                : prev.end_date;
            let shipper = [];
            if (query.shipper && query.shipper.length > 0) {
                shipper = query.shipper;
            }
            let carrier = [];
            if (query.carrier && query.carrier.length > 0) {
                carrier = query.carrier;
            }
            const origin_address = query.origin_address;
            const destination_address = query.destination_address;
            let filterMetaData = {
                start_date: startDate,
                end_date: endDate,
                shipper,
                carrier,
            };
            if (origin_address)
                Object.assign(filterMetaData, { origin_address });
            if (destination_address)
                Object.assign(filterMetaData, { destination_address });
            if (query.quickpay_carrier)
                Object.assign(filterMetaData, {
                    quickpay_carrier: query.quickpay_carrier === 'true',
                });
            if (!query.hasOwnProperty('quickpay_carrier'))
                Object.assign(filterMetaData, {
                    quickpay_carrier: true,
                });
            return Object.assign({}, filterMetaData);
        });
        let carriers = [];
        if (query.carrier && query.carrier.length > 0) {
            const fetchedCarriers = await findUsers({ _id: query.carrier });
            carriers = [...fetchedCarriers.entity.items];
        }
        let shippers = [];
        if (query.shipper && query.shipper.length > 0) {
            const fetchedShippers = await findUsers({ _id: query.shipper });
            shippers = [...fetchedShippers.entity.items];
        }
        const origin_address = query.origin_address;
        const destination_address = query.destination_address;
        setFilterMetadata((prev) => {
            return Object.assign(Object.assign({}, prev), { carrier: [...carriers], shipper: [...shippers], origin_address: { address: origin_address }, destination_address: { address: destination_address } });
        });
        setLocationParsed(true);
    }
    function updateUrl(filters) {
        const [sort, sort_dir] = sortValue.split('__');
        history.push(`${thislocation.pathname}?${qs.stringify(Object.assign({ per_page: PER_PAGE, page,
            sort,
            sort_dir }, filters))}`);
    }
    function handlePageClick(selectedPage) {
        setPage(selectedPage);
    }
    function handleSort(e) {
        setSortValue(e.target.value);
        setPage(1);
    }
    function getMomentDate(date) {
        if (!date) {
            return null;
        }
        return moment(date);
    }
    function translateFilterMetadata(currentFilters) {
        return {
            carrier: [...currentFilters.carrier],
            shipper: [...currentFilters.shipper],
            start_date: getMomentDate(currentFilters.start_date).format(),
            end_date: getMomentDate(currentFilters.end_date).format(),
            origin_address: currentFilters.origin_address,
            destination_address: currentFilters.destination_address,
            quickpay_carrier: currentFilters.quickpay_carrier,
        };
    }
    async function fetchUnprocessedCarrierBaseCosts() {
        if (!isVisible) {
            return;
        }
        try {
            if (!SessionStore.isAdmin()) {
                setLoading(false);
                return;
            }
            const [sort, sort_dir] = sortValue.split('__');
            if (cancelTokenSource) {
                cancelRequest();
            }
            const source = axios.CancelToken.source();
            const cancelToken = source.token;
            setCancelTokenSource(source);
            const { data: { data: results }, } = await getUnprocessedCarrierBaseCosts({
                per_page: PER_PAGE,
                page,
                sort,
                sort_dir,
                filterMetadata: translateFilterMetadata(filterValues),
                cancelToken,
            });
            const { meta: { total: newTotal, pages: newPages }, data, } = results;
            const mappedItems = (data || []).map((item, i) => {
                const { count, _id: { carrier, carrier_base_cost, shipper, shipper_base_cost, currency, origin_address, destination_address, destination: { address: destinationAddress, street: destinationStreet, city: destinationCity, state: destinationState, zipcode: destinationZipcode, }, origin: { address: originAddress, street: originStreet, city: originCity, state: originState, zipcode: originZipcode, }, margin, approvalDueDate, carrierFscTotal, }, } = item;
                const id = `${carrier._id}-${shipper._id}-${destinationAddress}_${originAddress}_${shipper_base_cost}_${carrier_base_cost}_${count}_${i}`;
                return {
                    id,
                    count,
                    carrier,
                    carrier_base_cost,
                    shipper,
                    shipper_base_cost,
                    currency,
                    origin_address,
                    destination_address,
                    originAddress,
                    originStreet,
                    originCity,
                    originState,
                    originZipcode,
                    destinationAddress,
                    destinationStreet,
                    destinationCity,
                    destinationState,
                    destinationZipcode,
                    margin,
                    approvalDueDate,
                    carrierFscTotal,
                };
            });
            setTotal(newTotal);
            setPages(newPages);
            setItems(mappedItems);
        }
        catch (err) {
            console.error(err);
            if (!axios.isCancel(err)) {
                showToast(t('UNPROCESSED_CARRIER_COST'), t('FAILED_TO_FETCH_UNPROCESSED_CARRIER_COST'), 'error');
            }
        }
        finally {
            setLoading(false);
            setCancelTokenSource(null);
        }
    }
    function handleReloadActions() {
        fetchUnprocessedCarrierBaseCosts();
    }
    async function prepareReqBodyForCarrierBulkApproveRates() {
        const start_date = getMomentDate(filterValues.start_date).format();
        const end_date = getMomentDate(filterValues.end_date).format();
        const rows = selectedRates.map((rate) => {
            const { shipper: { _id: shipper }, carrier: { _id: carrier }, originAddress: origin_address, destinationAddress: destination_address, shipper_base_cost, carrier_base_cost, carrierFscTotal, approvalDueDate, } = rate;
            const params = {
                shipper,
                carrier,
                origin_address,
                destination_address,
                shipper_base_cost,
                carrier_base_cost,
                carrierFscTotal,
                approvalDueDate,
                start_date,
                end_date,
            };
            return params;
        });
        return { rows };
    }
    async function fetchUnprocessedShipmentList(options, filterOutSelected, selectedShipments, disableLoader) {
        try {
            const { rows } = await prepareReqBodyForCarrierBulkApproveRates();
            const requests = rows.map((row) => {
                const filterMetadata = Object.assign(Object.assign({}, translateFilterMetadata(filterValues)), row);
                return getUnprocessedShipmentList({
                    filterMetadata,
                    options,
                });
            });
            if (!disableLoader) {
                setShipmentsLoading(true);
            }
            const responses = await Promise.all(requests);
            let shipmentList = [];
            responses.forEach((response) => {
                shipmentList = [...shipmentList, ...response.shipments];
            });
            if (responses.length > 0) {
                const { total } = responses[0].meta || 0;
                setShipmentsTotal(Number(total));
            }
            if (filterOutSelected && selectedShipments.length > 0) {
                setUnapprovedShipmentList(shipmentList.filter((shipment) => !selectedShipments.includes(shipment._id)));
            }
            else {
                setUnapprovedShipmentList(shipmentList.filter((shipment) => !shipmentsQueuedForApproval.includes(shipment._id)));
            }
            if (!disableLoader) {
                setShipmentsLoading(false);
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    async function postBulkApproveRates(shipmentIds) {
        try {
            setLoading(true);
            setShowConfirmBulkApproveModal(false);
            let reqBody = await prepareReqBodyForCarrierBulkApproveRates();
            if (shipmentIds.length > 0) {
                reqBody.rows = reqBody.rows.map((row) => {
                    return Object.assign(Object.assign({}, row), { _id: shipmentIds });
                });
            }
            await postCarrierBulkApproveRates(reqBody);
            showToast(t('BULK_CARRIER_RATE_APPROVAL'), t('BULK_APPROVE_SUCCESS_MESSAGE'), 'success');
            handleReloadActions();
            setLoading(false);
        }
        catch (error) {
            showToast(t('BULK_CARRIER_RATE_APPROVAL'), t('BULK_APPROVE_FAILURE_MESSAGE'), 'error');
        }
    }
    function checkBulkAuditToolPermission() {
        if (!SessionStore.isAbleToAdjustCarrierBulkCost()) {
            showToast(t('PERMISSION_REQUIRED'), t('PERMISSION_REQUIRED'), 'error');
            return false;
        }
        return true;
    }
    function handleHideConfirmBulkApproveModal() {
        setSelectedRates([]);
        setUnapprovedShipmentList([]);
        setShowConfirmBulkApproveModal(false);
    }
    function handleEdit(item) {
        if (!checkBulkAuditToolPermission())
            return;
        setSelectedRates([item]);
        setShowEditRateModal(true);
    }
    function handleHideEditRateModal() {
        setSelectedRates([]);
        setShowEditRateModal(false);
    }
    async function handleBulkEditCarrierRate(bulkAdjustmentMetaData) {
        try {
            bulkAdjustmentMetaData.start_date = getMomentDate(filterValues.start_date).format();
            bulkAdjustmentMetaData.end_date = getMomentDate(filterValues.end_date).format();
            await bulkEditCarrierRate(bulkAdjustmentMetaData);
            showToast(`${t('CARRIER_RATE')}`, t('CARRIER_RATE_SUCCESSFULLY_UPDATED'), 'success');
            handleReloadActions();
        }
        catch (err) {
            showToast(`${t('CARRIER_RATE')}`, t('FAILED_TO_UPDATE_CARRIER_RATE'), 'error');
        }
    }
    function handleViewShipments(item) {
        setSelectedRates([item]);
        setShowViewShipmentsModal(true);
    }
    function handleHideViewShipmentsModal() {
        setShipmentsTotal(0);
        setSelectedRates([]);
        setUnapprovedShipmentList([]);
        setShipmentsQueuedForApproval[[]];
        setShowViewShipmentsModal(false);
    }
    async function handleSaveBulkApproval(shipmentIds) {
        await postBulkApproveRates(shipmentIds);
        setShipmentsQueuedForApproval(shipmentIds);
    }
    function renderSort() {
        return (React.createElement("div", { "data-testid": "div-eb6189f6", className: "sm:w-52 w-full" },
            React.createElement("select", { "data-testid": "select-f192aed3", className: "form-select", value: sortValue, onChange: handleSort },
                sortValue === 'Select' && (React.createElement("option", { "data-testid": "option-05876ee8", key: -1, value: "Select" }, t('SORT_BY'))),
                SORTING_OPTIONS.filter((o) => !o.hide).map((option, index) => (React.createElement("option", { "data-testid": `option-4c42def2-${index}`, key: option.value, value: option.value }, t(option.name)))))));
    }
    function handleToggleFilters() {
        setFiltersToggled((prev) => !prev);
    }
    function handleFilterActions(filterGroup, item, append = true) {
        const key = filterGroup.key;
        if (filterGroup.type === 'date') {
            if (!item) {
                if (key === 'start_date') {
                    item = moment().subtract(2, 'days');
                }
                else {
                    item = moment();
                }
            }
            updateUrl(translateFilterMetadata(Object.assign(Object.assign({}, filterValues), { [key]: item })));
            return;
        }
        if (filterGroup.type === 'location') {
            updateUrl(translateFilterMetadata(Object.assign(Object.assign({}, filterValues), { [key]: item.address })));
            setFilterMetadata((prev) => (Object.assign(Object.assign({}, prev), { [key]: item })));
            return;
        }
        if (append) {
            setFilterMetadata((prev) => (Object.assign(Object.assign({}, prev), { [key]: uniq([...prev[key], item]) })));
            updateUrl(translateFilterMetadata(Object.assign(Object.assign({}, filterValues), { [key]: uniq([...filterValues[key], item._id]) })));
        }
        else {
            setFilterMetadata((prev) => (Object.assign(Object.assign({}, prev), { [key]: prev[key].filter((el) => el._id !== item._id) })));
            updateUrl(translateFilterMetadata(Object.assign(Object.assign({}, filterValues), { [key]: filterValues[key].filter((id) => id !== item._id) })));
        }
    }
    const toggleQuickpayCarriers = (value) => {
        setFilterMetadata((prev) => {
            return Object.assign(Object.assign({}, prev), { quickpay_carrier: value });
        });
        updateUrl(translateFilterMetadata(Object.assign(Object.assign({}, filterValues), { quickpay_carrier: value })));
    };
    function updateSelectedFiltersCount() {
        const count = Object.keys(filterValues).reduce((count, key) => {
            if ((filterValues[key] || []).length > 0) {
                return count + 1;
            }
            if (key.includes('date') && filterValues[key]) {
                return count + 1;
            }
            if (key.includes('date') && filterValues[key]) {
                return count + 1;
            }
            if (key === 'origin' && filterValues[key]) {
                return count + 1;
            }
            if (key === 'destination' && filterValues[key]) {
                return count + 1;
            }
            return count;
        }, 0);
        setSelectedFiltersCount(count);
    }
    function renderActionBar() {
        return (React.createElement("div", { "data-testid": "div-aaa3e3dc", className: "mt-6" },
            React.createElement("div", { "data-testid": "div-a9236919", className: "w-full mx-auto p-4" },
                React.createElement("div", { "data-testid": "div-70baf739", className: "flex items-center justify-between flex-wrap -mx-4" },
                    React.createElement("div", { "data-testid": "div-4ccffa6c", className: "flex px-4 flex-1" }, selectedFiltersCount > 0 && (React.createElement("div", { "data-testid": "div-0c5fef2a", className: "flex flex-wrap items-center -mx-2" }, "\u00A0"))),
                    React.createElement("div", { "data-testid": "div-5e795fae", className: "flex items-center flex-wrap -mx-2 sm:justify-end px-4" },
                        React.createElement("div", { "data-testid": "div-0bbeeda7", className: "px-2 mb-2 sm:mb-0" },
                            React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-a7e53009", id: "toggle-quickpay-carriers", type: "checkbox", label: t('TOGGLE_QUICKPAY_CARRIERS'), value: filterValues.quickpay_carrier, onChange: toggleQuickpayCarriers })),
                        React.createElement("div", { "data-testid": "div-92b02d37", className: "order-3 flex-shrink-0 sm:order-2 sm:mt-0 sm:w-auto w-full px-2 mb-2 sm:mb-0" }, renderSort()),
                        React.createElement("div", { "data-testid": "div-75fa8697", className: "order-2 flex-shrink-0 sm:order-3 px-2 mb-2 sm:mb-0" },
                            selectedFiltersCount === 0 && (React.createElement(FilterButton, { dataTestId: "FilterButton-57e9b41d", onClick: handleToggleFilters, active: filtersToggled, size: "small" })),
                            selectedFiltersCount > 0 && (React.createElement("button", { "data-testid": "button-161732f1", className: "button button-hollow-green", onClick: handleToggleFilters },
                                React.createElement("div", { "data-testid": "div-79ae4bd4", className: "flex items-center space-x-1" },
                                    React.createElement("div", { "data-testid": "div-c2b7ae74", className: getCircleButtonClass({
                                            size: 'xsmall',
                                            color: 'brand',
                                        }) }, selectedFiltersCount),
                                    React.createElement("span", { "data-testid": "span-b1580713" }, t('FILTER', { count: 2 })))))))))));
    }
    function renderNeedPricingMnagerPrivilege() {
        return (React.createElement(Alert, { dataTestId: "Alert-e33b1d86", type: "warning" },
            React.createElement("p", { "data-testid": "p-3f1ffdf0" }, t('REQUIRED_PRICING_PRIVILEGES'))));
    }
    function renderEmptyList() {
        return (React.createElement("div", { "data-testid": "div-320e4c22", className: "p-4" }, t('EMPTY_STATE')));
    }
    if (!SessionStore.isAdmin()) {
        return (React.createElement("div", { "data-testid": "div-5307f38c", className: "p-4" },
            React.createElement(Alert, { dataTestId: "Alert-e770534e", title: t('REQUIRE_PERMISSION_TO_VIEW_PAGE'), type: "error" })));
    }
    function renderFiltersSidebar() {
        if (!filtersToggled) {
            return null;
        }
        return (React.createElement(BulkAuditFilters, { show: filtersToggled, filters: FILTERS, filterValues: filterValues, filterMetadata: filterMetadata, onFilterChange: handleFilterActions, onHide: () => setFiltersToggled(false) }));
    }
    const loadingText = `${t('LOADING')}`;
    return (React.createElement(React.Fragment, null,
        React.createElement(Container, { dataTestId: "Container-6ab70b9d", className: "min-h-screen  max-w-screen py-10" },
            React.createElement("h3", { "data-testid": "h3-b411512d" }, t('CARRIER_COST_BULK_AUDIT_TOOL')),
            React.createElement("div", { "data-testid": "div-f63e937b", className: "bg-white rounded-md shadow divide-y divide-solid divide-gray-200" },
                !SessionStore.isAbleToAdjustCarrierBulkCost() &&
                    renderNeedPricingMnagerPrivilege(),
                renderActionBar(),
                total > 0 && !loading && (React.createElement(BulkAuditList, { items: items, onPageClick: handlePageClick, onReloadNextActions: handleReloadActions, perPage: PER_PAGE, page: page, pages: pages, total: total, onViewShipments: handleViewShipments, onEdit: handleEdit })),
                showConfirmBulkApproveModal && (React.createElement(BulkApprovalModal, { selectedRates: selectedRates, showModal: showConfirmBulkApproveModal, onSave: postBulkApproveRates, onClose: handleHideConfirmBulkApproveModal })),
                loading && (React.createElement("div", { "data-testid": "div-7c075108", className: "p-4" },
                    React.createElement(LoadingCircle, { dataTestId: "LoadingCircle-0fd84a3c", text: loadingText }))),
                !total && !loading && renderEmptyList())),
        renderFiltersSidebar(),
        showEditRateModal && (React.createElement(EditLaneRateModal, { itemMetaData: selectedRates[0], showModal: showEditRateModal, onSave: handleBulkEditCarrierRate, onClose: handleHideEditRateModal })),
        showViewShipmentsModal && (React.createElement(ShipmentListModal, { dialogClassName: "w-11/12", shipments: unapprovedShipmentList, shipmentsLoading: shipmentsLoading, shipmentsTotal: shipmentsTotal, showModal: showViewShipmentsModal, onFilterChange: (options, filterOutSelected, selectedShipments, disableLoader) => fetchUnprocessedShipmentList(options, filterOutSelected, selectedShipments, disableLoader), onSave: handleSaveBulkApproval, onClose: handleHideViewShipmentsModal }))));
}
